import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import RotateDialogFields from "../../field/page/RotateDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	OrientationDetection,
	PageGroup,
	PageOrientation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxRotateRotate,
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface RotateDialogStatus extends PortalDialogState<ToolboxRotateRotate> {
	pages: number;
	currentPage: string;
}

export class RotateDialogDefinition extends PortalDialog<ToolboxRotateRotate,
	PortalDialogProperties<ToolboxRotateRotate>, RotateDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<RotateDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.rotatePages, Functions.getRotatePagesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ROTATE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["rotate-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.rotation.general"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxRotateRotate | undefined = this.state.parameter?.data;
		let errors: Map<RotateDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(RotateDialogFields.ROTATION)}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-rotation">
							<Translate {...this.props} data="rotation.angle.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-rotation"
									  value={FunctionHelper.applyValue(
										  parameter?.degrees, ToolboxRotateRotate.getDegreesDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("rotation.angle.cw", {ns: "dialog"}),
											  value: 90
										  },
										  {
											  label: Translate.value("rotation.angle.turn", {ns: "dialog"}),
											  value: 180
										  },
										  {
											  label: Translate.value("rotation.angle.ccw", {ns: "dialog"}),
											  value: 270
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  RotateDialogFields.ROTATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(RotateDialogFields.PAGERANGE) &&
					!this.state.fields?.contains(RotateDialogFields.DIRECTION)
				}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RotateDialogFields.PAGERANGE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="rotation.pages.label" namespace="dialog"/>
							<Info {...this.props} data="rotation.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(RotateDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxRotateRotate.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   RotateDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(RotateDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(RotateDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(RotateDialogFields.PAGERANGE) ? Translate.value(
								errors.get(RotateDialogFields.PAGERANGE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.rotation.filter"} namespace={"help"} markdown={true}/>
					</Helper>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(RotateDialogFields.PAGERANGE) ||
						!this.state.fields?.contains(RotateDialogFields.PAGES)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pages">
							<Translate {...this.props} data="rotation.even_odd.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-pages"
									  value={FunctionHelper.applyValue(
										  parameter?.pageGroup, ToolboxRotateRotate.getPageGroupDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("rotation.even_odd.none", {ns: "dialog"}),
											  value: PageGroup.All
										  },
										  {
											  label: Translate.value("rotation.even_odd.even", {ns: "dialog"}),
											  value: PageGroup.Even
										  }, {
											  label: Translate.value("rotation.even_odd.odd", {ns: "dialog"}),
											  value: PageGroup.Odd
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  RotateDialogFields.PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RotateDialogFields.DIRECTION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-direction">
							<Translate {...this.props} data="rotation.direction.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-direction"
									  value={FunctionHelper.applyValue(
										  parameter?.pageOrientation, ToolboxRotateRotate.getPageOrientationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "rotation.direction.none", {ns: "dialog"}
											  ),
											  value: PageOrientation.Any
										  },
										  {
											  label: Translate.value(
												  "rotation.direction.portrait", {ns: "dialog"}
											  ),
											  value: PageOrientation.Portrait
										  },
										  {
											  label: Translate.value(
												  "rotation.direction.landscape", {ns: "dialog"}
											  ),
											  value: PageOrientation.Landscape
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  RotateDialogFields.DIRECTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(RotateDialogFields.ORIENTATION) ||
							 parameter?.pageOrientation === PageOrientation.Any
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-orientation">
							<Translate {...this.props} data="rotation.orientation.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-orientation"
									  value={FunctionHelper.applyValue(
										  parameter?.orientationDetectMode,
										  ToolboxRotateRotate.getOrientationDetectModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "rotation.orientation.value", {ns: "dialog"}
											  ),
											  value: OrientationDetection.Value
										  },
										  {
											  label: Translate.value(
												  "rotation.orientation.dimensions", {ns: "dialog"}
											  ),
											  value: OrientationDetection.Dimensions
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  RotateDialogFields.ORIENTATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: RotateDialogFields, value: any): void {
		let parameter: ToolboxRotateRotate = this.state.parameter!.data;
		let errors: Map<RotateDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case RotateDialogFields.ROTATION:
				parameter.degrees = value;
				break;
			case RotateDialogFields.DIRECTION:
				parameter.pageOrientation = value;
				break;
			case RotateDialogFields.PAGES:
				parameter.pageGroup = value;
				break;
			case RotateDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
			case RotateDialogFields.ORIENTATION:
				parameter.orientationDetectMode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-rotate-90 small"}/>
					<Translate {...this.props} data="rotation.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canAssemble, Security.getCanAssembleDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxRotateRotate | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			rotate: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.rotate", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ROTATE;
	}

	protected getDefaultParameter(): Parameter<ToolboxRotateRotate> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const RotateDialog = React.memo(RotateDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxRotateRotate>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxRotateRotate>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RotateDialog;